import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import '../styles/Dashboard.css';
import { professionsCNP2021 } from '../data/professionsCNP2021';
import NumericInput from './NumericInput';
import { performGlobalSearch } from '../services/searchAPI';
import jsPDF from 'jspdf';
import { useSettings } from '../contexts/SettingsContext';
import settingsService from '../services/settingsService';
import { getPdfBrand } from '../utils/pdfBranding';

const PSUQCalculator = () => {
  const { settings } = useSettings();
  const [hasSpouse, setHasSpouse] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [showProfessionGuide, setShowProfessionGuide] = useState(false);
  const [professionSearch, setProfessionSearch] = useState('');
  
  // Recherche client/lead
  const [personQuery, setPersonQuery] = useState('');
  const [personResults, setPersonResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  // Mapper les professions avec catégories
  const allProfessions = useMemo(() => {
    return professionsCNP2021.map(prof => {
      let categorie = 'Autres';
      const codePrefix = prof.code.substring(0, 2);
      
      if (codePrefix === '00' || codePrefix === '10') categorie = 'Gestion';
      else if (['11', '12', '13', '14'].includes(codePrefix)) categorie = 'Affaires et finance';
      else if (['20', '21', '22'].includes(codePrefix)) categorie = 'Sciences naturelles et appliquées';
      else if (['30', '31', '32', '33'].includes(codePrefix)) categorie = 'Santé';
      else if (['40', '41', '42', '43', '44', '45'].includes(codePrefix)) categorie = 'Enseignement et services gouvernementaux';
      else if (['50', '51', '52', '53', '54', '55'].includes(codePrefix)) categorie = 'Arts, culture et sports';
      else if (['60', '62', '63', '64', '65'].includes(codePrefix)) categorie = 'Ventes et services';
      else if (['70', '72', '73', '74', '75'].includes(codePrefix)) categorie = 'Métiers et transport';
      else if (['80', '82', '83', '84', '85'].includes(codePrefix)) categorie = 'Ressources naturelles et agriculture';
      else if (['90', '92', '93', '94', '95'].includes(codePrefix)) categorie = 'Fabrication et services d\'utilité publique';
      
      return { ...prof, categorie };
    });
  }, []);
  
  // Type de test de français
  const [frenchTestType, setFrenchTestType] = useState('');
  const [spouseFrenchTestType, setSpouseFrenchTestType] = useState('');
  
  // Capital humain
  const [frenchOralComp, setFrenchOralComp] = useState('0');
  const [frenchOralProd, setFrenchOralProd] = useState('0');
  const [frenchWrittenComp, setFrenchWrittenComp] = useState('0');
  const [frenchWrittenProd, setFrenchWrittenProd] = useState('0');
  
  // Scores bruts (pour les tests de français)
  const [rawScoreOralComp, setRawScoreOralComp] = useState('');
  const [rawScoreOralProd, setRawScoreOralProd] = useState('');
  const [rawScoreWrittenComp, setRawScoreWrittenComp] = useState('');
  const [rawScoreWrittenProd, setRawScoreWrittenProd] = useState('');
  const [age, setAge] = useState('');
  const [workExp, setWorkExp] = useState('0');
  const [education, setEducation] = useState('none');
  
  // Besoins du Québec
  const [professionDiag, setProfessionDiag] = useState('equilibre');
  const [professionExpQc, setProfessionExpQc] = useState('0');
  const [quebecDiploma, setQuebecDiploma] = useState('none');
  const [workExpQc, setWorkExpQc] = useState('0');
  const [residenceCMM, setResidenceCMM] = useState('0');
  const [workExpOutsideCMM, setWorkExpOutsideCMM] = useState('0');
  const [studyOutsideCMM, setStudyOutsideCMM] = useState('0');
  const [jobOffer, setJobOffer] = useState('none');
  const [professionAuth, setProfessionAuth] = useState(false);
  
  // Facteurs d'adaptation
  const [studyQc, setStudyQc] = useState('none');
  const [studyQcDuration, setStudyQcDuration] = useState('0');
  const [relative, setRelative] = useState('none');
  
  // Conjoint
  const [spouseFrenchOralComp, setSpouseFrenchOralComp] = useState('0');
  const [spouseFrenchOralProd, setSpouseFrenchOralProd] = useState('0');
  const [spouseFrenchWrittenComp, setSpouseFrenchWrittenComp] = useState('0');
  const [spouseFrenchWrittenProd, setSpouseFrenchWrittenProd] = useState('0');
  
  // Scores bruts conjoint
  const [rawScoreSpouseOralComp, setRawScoreSpouseOralComp] = useState('');
  const [rawScoreSpouseOralProd, setRawScoreSpouseOralProd] = useState('');
  const [rawScoreSpouseWrittenComp, setRawScoreSpouseWrittenComp] = useState('');
  const [rawScoreSpouseWrittenProd, setRawScoreSpouseWrittenProd] = useState('');
  const [spouseAge, setSpouseAge] = useState('');
  const [spouseWorkExpQc, setSpouseWorkExpQc] = useState('0');
  const [spouseEducation, setSpouseEducation] = useState('none');
  const [spouseQuebecDiploma, setSpouseQuebecDiploma] = useState('none');

  // useEffect pour la recherche client/lead
  useEffect(() => {
    if (!personQuery || personQuery.trim().length < 2) {
      setPersonResults([]);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await performGlobalSearch(personQuery.trim());
        const data = res?.data || {};
        const leads = (data.leads || []).map(l => ({
          id: l.id,
          type: 'lead',
          label: l.title || l.subtitle || 'Lead',
          raw: l
        }));
        const clients = (data.clients || []).map(c => ({
          id: c.id,
          type: 'client',
          label: c.title || c.subtitle || 'Client',
          raw: c
        }));
        setPersonResults([ ...clients.slice(0,5), ...leads.slice(0,5) ]);
      } catch (e) {
        setPersonResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => searchTimeoutRef.current && clearTimeout(searchTimeoutRef.current);
  }, [personQuery]);

  // Handlers mémorisés pour les scores bruts
  const handleRawScoreOralCompChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreOralComp(value);
  }, []);

  const handleRawScoreOralProdChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreOralProd(value);
  }, []);

  const handleRawScoreWrittenCompChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreWrittenComp(value);
  }, []);

  const handleRawScoreWrittenProdChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreWrittenProd(value);
  }, []);

  const handleRawScoreSpouseOralCompChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreSpouseOralComp(value);
  }, []);

  const handleRawScoreSpouseOralProdChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreSpouseOralProd(value);
  }, []);

  const handleRawScoreSpouseWrittenCompChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreSpouseWrittenComp(value);
  }, []);

  const handleRawScoreSpouseWrittenProdChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRawScoreSpouseWrittenProd(value);
  }, []);

  // Fonction pour convertir le score du test en niveau de l'échelle québécoise
  const convertTestScoreToQuebecLevel = (score, testType, competence) => {
    const scoreNum = parseInt(score);
    if (!scoreNum) return 0;

    // TEF/TEFAQ et TEF-Canada (avant 11 déc 2023) - Même échelle
    if (testType === 'tef' || testType === 'tefaq' || testType === 'tef-canada-old') {
      if (scoreNum >= 100 && scoreNum <= 199) return 2; // A1 (1-2)
      if (scoreNum >= 200 && scoreNum <= 259) return 3; // A2
      if (scoreNum >= 260 && scoreNum <= 299) return 4; // A2
      if (scoreNum >= 300 && scoreNum <= 399) return 6; // B1 (5-6)
      if (scoreNum >= 400 && scoreNum <= 499) return 8; // B2 (7-8)
      if (scoreNum >= 500 && scoreNum <= 599) return 10; // C1 (9-10)
      if (scoreNum >= 600) return 12; // C2 (11-12)
    }
    
    // TEF-Canada (à partir du 11 déc 2023) - Expression orale séparée
    if (testType === 'tef-canada-new') {
      if (competence === 'oral-prod') {
        // Expression orale
        if (scoreNum >= 100 && scoreNum <= 199) return 2;
        if (scoreNum >= 200 && scoreNum <= 259) return 3;
        if (scoreNum >= 260 && scoreNum <= 299) return 4;
        if (scoreNum >= 300 && scoreNum <= 399) return 6;
        if (scoreNum >= 400 && scoreNum <= 499) return 8;
        if (scoreNum >= 500 && scoreNum <= 599) return 10;
        if (scoreNum >= 600) return 12;
      } else if (competence === 'written') {
        // Expression écrite
        if (scoreNum >= 100 && scoreNum <= 199) return 2;
        if (scoreNum >= 200 && scoreNum <= 259) return 3;
        if (scoreNum >= 260 && scoreNum <= 299) return 4;
        if (scoreNum >= 300 && scoreNum <= 399) return 6;
        if (scoreNum >= 400 && scoreNum <= 499) return 8;
        if (scoreNum >= 500 && scoreNum <= 599) return 10;
        if (scoreNum >= 600) return 12;
      } else {
        // Compréhension orale et écrite
        if (scoreNum >= 100 && scoreNum <= 199) return 2;
        if (scoreNum >= 200 && scoreNum <= 259) return 3;
        if (scoreNum >= 260 && scoreNum <= 299) return 4;
        if (scoreNum >= 300 && scoreNum <= 399) return 6;
        if (scoreNum >= 400 && scoreNum <= 499) return 8;
        if (scoreNum >= 500 && scoreNum <= 599) return 10;
        if (scoreNum >= 600) return 12;
      }
    }

    // TCF/TCF-Québec
    if (testType === 'tcf' || testType === 'tcf-quebec') {
      // Compréhension orale et écrite
      if (competence === 'comp') {
        if (scoreNum >= 101 && scoreNum <= 199) return 2;
        if (scoreNum >= 200 && scoreNum <= 259) return 3;
        if (scoreNum >= 260 && scoreNum <= 299) return 4;
        if (scoreNum >= 300 && scoreNum <= 399) return 6;
        if (scoreNum >= 400 && scoreNum <= 499) return 8;
        if (scoreNum >= 500 && scoreNum <= 599) return 10;
        if (scoreNum >= 600) return 12;
      } else {
        // Expression orale et écrite
        if (scoreNum === 1) return 2; // A1
        if (scoreNum >= 2 && scoreNum <= 3) return 3; // A2
        if (scoreNum >= 4 && scoreNum <= 5) return 4; // A2
        if (scoreNum >= 6 && scoreNum <= 9) return 6; // B1
        if (scoreNum >= 10 && scoreNum <= 13) return 8; // B2
        if (scoreNum >= 14 && scoreNum <= 17) return 10; // C1
        if (scoreNum >= 18) return 12; // C2
      }
    }

    // TCF-Canada
    if (testType === 'tcf-canada') {
      if (competence === 'comp') {
        // Compréhension
        if (scoreNum >= 101 && scoreNum <= 199) return 2;
        if (scoreNum >= 200 && scoreNum <= 259) return 3;
        if (scoreNum >= 260 && scoreNum <= 299) return 4;
        if (scoreNum >= 300 && scoreNum <= 399) return 6;
        if (scoreNum >= 400 && scoreNum <= 499) return 8;
        if (scoreNum >= 500 && scoreNum <= 599) return 10;
        if (scoreNum >= 600) return 12;
      } else {
        // Expression
        if (scoreNum === 1) return 2;
        if (scoreNum >= 2 && scoreNum <= 3) return 3;
        if (scoreNum >= 4 && scoreNum <= 5) return 4;
        if (scoreNum >= 6 && scoreNum <= 9) return 6;
        if (scoreNum >= 10 && scoreNum <= 13) return 8;
        if (scoreNum >= 14 && scoreNum <= 17) return 10;
        if (scoreNum >= 18) return 12;
      }
    }

    return 0;
  };

  // Calculs - Basé sur le tableau de correspondance MIFI (29 novembre 2024)
  const getFrenchPoints = (level, hasSpouse, competence) => {
    const maxPoints = competence === 'oral' ? (hasSpouse ? 40 : 50) : (hasSpouse ? 40 : 50);
    const levelNum = parseInt(level);
    
    // Échelle québécoise: 1-2 (A1), 3 (A2), 4 (A2), 5-6 (B1), 7-8 (B2), 9-10 (C1), 11-12 (C2)
    if (levelNum >= 1 && levelNum <= 4) return 0; // A1-A2: 0 points
    if (levelNum >= 5 && levelNum <= 6) return hasSpouse ? 30 : 38; // B1: 30/38 points
    if (levelNum >= 7 && levelNum <= 8) return hasSpouse ? 35 : 44; // B2: 35/44 points
    if (levelNum >= 9 && levelNum <= 10) return hasSpouse ? 38 : 50; // C1: 38/50 points
    if (levelNum >= 11) return maxPoints; // C2: 40/50 points
    return 0;
  };

  const frenchPoints = useMemo(() => {
    // Si un type de test est sélectionné (autre que manuel), convertir les scores bruts
    if (frenchTestType && frenchTestType !== 'manual') {
      const oralCompLevel = convertTestScoreToQuebecLevel(rawScoreOralComp, frenchTestType, 'comp');
      const oralProdLevel = convertTestScoreToQuebecLevel(rawScoreOralProd, frenchTestType, 
        frenchTestType === 'tef-canada-new' ? 'oral-prod' : 'expr');
      const writtenCompLevel = convertTestScoreToQuebecLevel(rawScoreWrittenComp, frenchTestType, 'comp');
      const writtenProdLevel = convertTestScoreToQuebecLevel(rawScoreWrittenProd, frenchTestType, 'written');
      
      return getFrenchPoints(oralCompLevel, hasSpouse, 'oral') +
             getFrenchPoints(oralProdLevel, hasSpouse, 'oral') +
             getFrenchPoints(writtenCompLevel, hasSpouse, 'written') +
             getFrenchPoints(writtenProdLevel, hasSpouse, 'written');
    }
    
    // Mode manuel : utiliser directement les niveaux
    return getFrenchPoints(frenchOralComp, hasSpouse, 'oral') +
           getFrenchPoints(frenchOralProd, hasSpouse, 'oral') +
           getFrenchPoints(frenchWrittenComp, hasSpouse, 'written') +
           getFrenchPoints(frenchWrittenProd, hasSpouse, 'written');
  }, [frenchOralComp, frenchOralProd, frenchWrittenComp, frenchWrittenProd, rawScoreOralComp, rawScoreOralProd, rawScoreWrittenComp, rawScoreWrittenProd, hasSpouse, frenchTestType]);

  const agePoints = useMemo(() => {
    const ageNum = parseInt(age);
    if (!ageNum || ageNum < 18) return 0;
    const maxPoints = hasSpouse ? 100 : 120;
    const points = {
      18: hasSpouse ? 90 : 110, 20: maxPoints, 31: hasSpouse ? 95 : 110,
      32: hasSpouse ? 90 : 100, 33: hasSpouse ? 81 : 90, 34: hasSpouse ? 72 : 80,
      35: hasSpouse ? 68 : 75, 36: hasSpouse ? 63 : 70, 37: hasSpouse ? 59 : 65,
      38: hasSpouse ? 54 : 60, 39: hasSpouse ? 50 : 55, 40: hasSpouse ? 45 : 50,
      41: hasSpouse ? 36 : 40, 42: hasSpouse ? 27 : 30, 43: hasSpouse ? 18 : 20, 44: hasSpouse ? 9 : 10
    };
    
    if (ageNum >= 18 && ageNum <= 19) return points[18];
    if (ageNum >= 20 && ageNum <= 30) return points[20];
    if (ageNum >= 45) return 0;
    return points[ageNum] || 0;
  }, [age, hasSpouse]);

  const workExpPoints = useMemo(() => {
    const months = parseInt(workExp);
    const maxPoints = hasSpouse ? 50 : 70;
    if (months < 12) return 0;
    if (months >= 12 && months <= 23) return hasSpouse ? 15 : 20;
    if (months >= 24 && months <= 35) return hasSpouse ? 30 : 40;
    if (months >= 36 && months <= 47) return hasSpouse ? 35 : 50;
    if (months >= 48) return maxPoints;
    return 0;
  }, [workExp, hasSpouse]);

  const educationPoints = useMemo(() => {
    const points = {
      secondaire: hasSpouse ? 11 : 13,
      sec_prof_600_899: hasSpouse ? 11 : 13,
      sec_prof_900_qc: hasSpouse ? 22 : 26,
      sec_prof_1an: hasSpouse ? 22 : 26,
      post_gen_2ans: hasSpouse ? 33 : 39,
      post_tech_900_qc: hasSpouse ? 44 : 52,
      post_tech_1_2ans: hasSpouse ? 44 : 52,
      post_tech_3ans: hasSpouse ? 66 : 78,
      univ_1er_1an: hasSpouse ? 66 : 78,
      univ_1er_2ans: hasSpouse ? 77 : 91,
      univ_1er_3_4ans: hasSpouse ? 88 : 104,
      univ_1er_5ans: hasSpouse ? 93 : 110,
      univ_2e_1an: hasSpouse ? 93 : 110,
      univ_2e_2ans: hasSpouse ? 99 : 117,
      univ_med_2ans: hasSpouse ? 110 : 130,
      univ_3e: hasSpouse ? 110 : 130
    };
    return points[education] || 0;
  }, [education, hasSpouse]);

  const capitalHumain = frenchPoints + agePoints + workExpPoints + educationPoints;

  const professionPoints = useMemo(() => {
    const months = parseInt(professionExpQc);
    if (months < 12) return 0;
    
    const points = {
      equilibre: { 12: 5, 24: 10, 36: 15, 48: 25 },
      leger: { 12: 70, 24: 80, 36: 90, 48: 100 },
      deficit: { 12: 90, 24: 100, 36: 110, 48: 120 }
    };
    
    if (months >= 12 && months <= 23) return points[professionDiag][12];
    if (months >= 24 && months <= 35) return points[professionDiag][24];
    if (months >= 36 && months <= 47) return points[professionDiag][36];
    if (months >= 48) return points[professionDiag][48];
    return 0;
  }, [professionExpQc, professionDiag]);

  const quebecDiplomaPoints = useMemo(() => {
    const points = {
      secondaire: 20, 
      sec_prof_600_899: 20, 
      sec_prof_900: 40,
      post_gen_2ans: 60, 
      post_tech_900: 80, 
      post_tech_3ans: 120,
      univ_1er_1an: 120, 
      univ_1er_2ans: 140, 
      univ_1er_3_4ans: 160,
      univ_1er_5ans: 170, 
      univ_2e_1an: 170, 
      univ_2e_2ans: 180,
      univ_med_2ans: 200, 
      univ_3e: 200
    };
    return points[quebecDiploma] || 0;
  }, [quebecDiploma]);

  const workExpQcPoints = useMemo(() => {
    const months = parseInt(workExpQc);
    if (months < 12) return 0;
    if (months >= 12 && months <= 23) return 40;
    if (months >= 24 && months <= 35) return 80;
    if (months >= 36 && months <= 47) return 120;
    if (months >= 48) return 160;
    return 0;
  }, [workExpQc]);

  const getOutsideCMMPoints = (months, type) => {
    const maxPoints = type === 'residence' ? 40 : type === 'work' ? 60 : 20;
    if (months < 6) return 0;
    if (months >= 6 && months <= 11) return type === 'residence' ? 6 : type === 'work' ? 9 : 3;
    if (months >= 12 && months <= 23) return type === 'residence' ? 16 : type === 'work' ? 24 : 8;
    if (months >= 24 && months <= 35) return type === 'residence' ? 24 : type === 'work' ? 36 : 12;
    if (months >= 36 && months <= 47) return type === 'residence' ? 32 : type === 'work' ? 48 : 16;
    if (months >= 48) return maxPoints;
    return 0;
  };

  const outsideCMMPoints = useMemo(() => {
    return getOutsideCMMPoints(parseInt(residenceCMM), 'residence') +
           getOutsideCMMPoints(parseInt(workExpOutsideCMM), 'work') +
           getOutsideCMMPoints(parseInt(studyOutsideCMM), 'study');
  }, [residenceCMM, workExpOutsideCMM, studyOutsideCMM]);

  const jobOfferPoints = jobOffer === 'outside_cmm' ? 50 : jobOffer === 'inside_cmm' ? 30 : 0;
  const professionAuthPoints = professionAuth ? 50 : 0;

  const besoinsQuebec = professionPoints + quebecDiplomaPoints + workExpQcPoints + 
                         outsideCMMPoints + jobOfferPoints + professionAuthPoints;

  const studyQcPoints = useMemo(() => {
    const months = parseInt(studyQcDuration);
    if (studyQc === 'none' || months < 6) return 0;
    
    if (studyQc === 'completed') {
      if (months >= 6 && months <= 11) return 1;
      if (months >= 12 && months <= 23) return 3;
      if (months >= 24 && months <= 35) return 5;
      if (months >= 36 && months <= 47) return 8;
      if (months >= 48) return 10;
    } else if (studyQc === 'ongoing') {
      if (months >= 6 && months <= 11) return 5;
      if (months >= 12 && months <= 23) return 12;
      if (months >= 24 && months <= 35) return 18;
      if (months >= 36 && months <= 47) return 24;
      if (months >= 48) return 30;
    }
    return 0;
  }, [studyQc, studyQcDuration]);

  const relativePoints = relative === 'direct' ? 10 : relative === 'spouse' ? 5 : 0;

  const spouseFrenchPoints = useMemo(() => {
    if (!hasSpouse) return 0;
    
    const getPoints = (level) => {
      const levelNum = parseInt(level);
      // Échelle québécoise pour conjoint: 1-4 (A1-A2), 5-6 (B1), 7-8 (B2), 9-10 (C1), 11-12 (C2)
      if (levelNum >= 1 && levelNum <= 4) return 0; // A1-A2: 0 points
      if (levelNum >= 5 && levelNum <= 6) return 6; // B1: 6 points
      if (levelNum >= 7 && levelNum <= 8) return 8; // B2: 8 points
      if (levelNum >= 9 && levelNum <= 10) return 9; // C1: 9 points
      if (levelNum >= 11) return 10; // C2: 10 points
      return 0;
    };
    
    // Si un type de test est sélectionné (autre que manuel), convertir les scores bruts
    if (spouseFrenchTestType && spouseFrenchTestType !== 'manual') {
      const oralCompLevel = convertTestScoreToQuebecLevel(rawScoreSpouseOralComp, spouseFrenchTestType, 'comp');
      const oralProdLevel = convertTestScoreToQuebecLevel(rawScoreSpouseOralProd, spouseFrenchTestType, 
        spouseFrenchTestType === 'tef-canada-new' ? 'oral-prod' : 'expr');
      const writtenCompLevel = convertTestScoreToQuebecLevel(rawScoreSpouseWrittenComp, spouseFrenchTestType, 'comp');
      const writtenProdLevel = convertTestScoreToQuebecLevel(rawScoreSpouseWrittenProd, spouseFrenchTestType, 'written');
      
      return getPoints(oralCompLevel) + getPoints(oralProdLevel) +
             getPoints(writtenCompLevel) + getPoints(writtenProdLevel);
    }
    
    // Mode manuel : utiliser directement les niveaux
    return getPoints(spouseFrenchOralComp) + getPoints(spouseFrenchOralProd) +
           getPoints(spouseFrenchWrittenComp) + getPoints(spouseFrenchWrittenProd);
  }, [hasSpouse, spouseFrenchOralComp, spouseFrenchOralProd, spouseFrenchWrittenComp, spouseFrenchWrittenProd, rawScoreSpouseOralComp, rawScoreSpouseOralProd, rawScoreSpouseWrittenComp, rawScoreSpouseWrittenProd, spouseFrenchTestType]);

  const spouseAgePoints = useMemo(() => {
    if (!hasSpouse) return 0;
    const ageNum = parseInt(spouseAge);
    if (!ageNum || ageNum < 16) return 0;
    const points = {
      16: 18, 20: 20, 31: 18, 32: 17, 33: 16, 34: 15, 35: 14,
      36: 12, 37: 10, 38: 8, 39: 7, 40: 6, 41: 5, 42: 4, 43: 3, 44: 2
    };
    if (ageNum >= 16 && ageNum <= 19) return 18;
    if (ageNum >= 20 && ageNum <= 30) return 20;
    if (ageNum >= 45) return 0;
    return points[ageNum] || 0;
  }, [hasSpouse, spouseAge]);

  const spouseWorkExpQcPoints = useMemo(() => {
    if (!hasSpouse) return 0;
    const months = parseInt(spouseWorkExpQc);
    if (months < 6) return 0;
    if (months >= 6 && months <= 11) return 5;
    if (months >= 12 && months <= 23) return 10;
    if (months >= 24 && months <= 35) return 15;
    if (months >= 36 && months <= 47) return 23;
    if (months >= 48) return 30;
    return 0;
  }, [hasSpouse, spouseWorkExpQc]);

  const spouseEducationPoints = useMemo(() => {
    if (!hasSpouse) return 0;
    const points = {
      secondaire: 2, 
      sec_prof_600_899: 2, 
      sec_prof_900_qc: 4, 
      sec_prof_1an: 4,
      post_gen_2ans: 6, 
      post_tech_900_qc: 8, 
      post_tech_1_2ans: 8, 
      post_tech_3ans: 12,
      univ_1er_1an: 12, 
      univ_1er_2ans: 14, 
      univ_1er_3_4ans: 16, 
      univ_1er_5ans: 17,
      univ_2e_1an: 17, 
      univ_2e_2ans: 18, 
      univ_med_2ans: 20, 
      univ_3e: 20
    };
    return points[spouseEducation] || 0;
  }, [hasSpouse, spouseEducation]);

  const spouseQuebecDiplomaPoints = useMemo(() => {
    if (!hasSpouse) return 0;
    const points = {
      secondaire: 3, 
      sec_prof_600_899: 3, 
      sec_prof_900: 6, 
      post_gen_2ans: 9,
      post_tech_900: 12, 
      post_tech_3ans: 18, 
      univ_1er_1an: 18, 
      univ_1er_2ans: 21,
      univ_1er_3_4ans: 24, 
      univ_1er_5ans: 25, 
      univ_2e_1an: 25, 
      univ_2e_2ans: 27,
      univ_med_2ans: 30, 
      univ_3e: 30
    };
    return points[spouseQuebecDiploma] || 0;
  }, [hasSpouse, spouseQuebecDiploma]);

  const facteursAdaptation = studyQcPoints + relativePoints + spouseFrenchPoints + 
                              spouseAgePoints + spouseWorkExpQcPoints + 
                              spouseEducationPoints + spouseQuebecDiplomaPoints;

  const totalPoints = capitalHumain + besoinsQuebec + facteursAdaptation;
  
  const filteredProfessions = useMemo(() => {
    if (!professionSearch) return allProfessions;
    const search = professionSearch.toLowerCase();
    return allProfessions.filter(p => 
      p.titre.toLowerCase().includes(search) ||
      p.code.includes(search) ||
      p.categorie.toLowerCase().includes(search)
    );
  }, [professionSearch]);
  
  const professionsByCategory = useMemo(() => {
    const grouped = {};
    filteredProfessions.forEach(prof => {
      if (!grouped[prof.categorie]) {
        grouped[prof.categorie] = [];
      }
      grouped[prof.categorie].push(prof);
    });
    return grouped;
  }, [filteredProfessions]);

  // Keep the section component identity stable while scores are being entered.
  // Otherwise React unmounts the open section on every score update and the input loses focus.
  const Section = useCallback(({ title, icon, children, points, maxPoints, id }) => {
    const isExpanded = expandedSection === id;
    return (
      <div className="card" style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => setExpandedSection(isExpanded ? null : id)}
          style={{
            width: '100%',
            background: '#f0f7ff',
            border: 'none',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            borderRadius: '8px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className={icon} style={{ color: '#2563eb' }}></i>
            <span style={{ fontWeight: '600' }}>{title}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#2563eb' }}>
              {points}/{maxPoints} points
            </span>
            <span>{isExpanded ? '−' : '+'}</span>
          </div>
        </button>
        {isExpanded && (
          <div style={{ padding: '1rem' }}>
            {children}
          </div>
        )}
      </div>
    );
  }, [expandedSection]);

  const generatePDF = async () => {
    const doc = new jsPDF();
    const logoUrl = settingsService.getLogoUrl(true, settings.logo);
    const { companyName, primaryColor } = await getPdfBrand({
      companyName: settings.nom,
      logoUrl
    });
    
    // Palette de couleurs professionnelle
    const accentColor = primaryColor; // Couleur de l'entreprise connectée
    const successColor = [16, 124, 16]; // Vert professionnel
    const warningColor = [255, 152, 0]; // Orange
    const textColor = [33, 33, 33]; // Noir presque pur
    const lightGray = [245, 245, 245]; // Gris très clair
    const mediumGray = [128, 128, 128]; // Gris moyen
    
    const getImageBase64 = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function() {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 200;
          let width = this.width;
          let height = this.height;
          
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(this, 0, 0, width, height);
          
          try {
            const dataURL = canvas.toDataURL('image/png', 0.8);
            resolve(dataURL);
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = reject;
        img.src = url;
      });
    };
    
    // Logo en filigrane
    try {
      const logoBase64 = await getImageBase64(logoUrl);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const logoSize = 100;
      const x = (pageWidth - logoSize) / 2;
      const y = (pageHeight - logoSize) / 2;
      doc.setGState(new doc.GState({opacity: 0.05}));
      doc.addImage(logoBase64, 'PNG', x, y, logoSize, logoSize);
      doc.setGState(new doc.GState({opacity: 1.0}));
    } catch (error) {
      console.log('Logo non chargé:', error);
    }
    
    // En-tête professionnel avec dégradé simulé
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 45, 'F');
    doc.setFillColor(...accentColor);
    doc.rect(0, 0, 210, 2, 'F'); // Ligne accent en haut
    
    // Nom de l'entreprise connectée
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(companyName, 15, 18);
    
    // Ligne de séparation
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    doc.line(15, 22, 195, 22);
    
    // Titre du document
    doc.setFontSize(14);
    doc.setFont(undefined, 'normal');
    doc.text('RAPPORT D\'ÉVALUATION', 15, 30);
    
    doc.setFontSize(11);
    doc.text('Programme de sélection des travailleurs qualifiés du Québec (PSTQ)', 15, 38);

    // Informations client et date dans un encadré (version compacte)
    let yPosition = 48;
    
    // Encadré d'information
    doc.setFillColor(...lightGray);
    doc.setDrawColor(...mediumGray);
    doc.setLineWidth(0.3);
    doc.rect(15, yPosition, 180, selectedPerson?.label ? 16 : 10, 'FD');
    
    doc.setTextColor(...textColor);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Date d'émission : ${new Date().toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`, 20, yPosition + 5);
    
    if (selectedPerson?.label) {
      doc.setFont(undefined, 'bold');
      doc.text(`Candidat : ${selectedPerson.label}`, 20, yPosition + 10);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...mediumGray);
      doc.text(`Type : ${selectedPerson.type === 'client' ? 'Client' : 'Prospect'}`, 20, yPosition + 14);
      yPosition += 19;
    } else {
      yPosition += 13;
    }
    
    doc.setTextColor(...textColor);
    
    // Score total dans un encadré mis en évidence (version compacte)
    yPosition += 3;
    const scorePercentage = (totalPoints / 1400 * 100).toFixed(1);
    const scoreColor = totalPoints >= 560 ? successColor : (totalPoints >= 400 ? warningColor : [220, 53, 69]);
    
    doc.setFillColor(...scoreColor);
    doc.rect(15, yPosition, 180, 20, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text(`${totalPoints} / 1400 points`, 105, yPosition + 10, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Score obtenu : ${scorePercentage}%`, 105, yPosition + 16, { align: 'center' });
    
    yPosition += 24;
    
    // Note d'interprétation
    doc.setFontSize(8);
    doc.setTextColor(...mediumGray);
    doc.setFont(undefined, 'italic');
    const interpretationText = totalPoints >= 560 
      ? '✓ Excellent score - Fortes chances de sélection' 
      : totalPoints >= 400 
      ? '⚠ Score moyen - Amélioration possible' 
      : '✗ Score insuffisant - Révision recommandée';
    doc.text(interpretationText, 105, yPosition, { align: 'center' });
    
    yPosition += 7;
    
    // Titre de section
    doc.setTextColor(...primaryColor);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('DÉTAIL DE L\'ÉVALUATION', 20, yPosition);
    
    doc.setDrawColor(...accentColor);
    doc.setLineWidth(0.8);
    doc.line(15, yPosition + 1, 195, yPosition + 1);
    
    yPosition += 7;
    
    // 1. CAPITAL HUMAIN - Version compacte
    doc.setFillColor(...primaryColor);
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.rect(15, yPosition, 180, 8, 'FD');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(11);
    doc.text(`1. CAPITAL HUMAIN`, 20, yPosition + 5.5);
    doc.text(`${capitalHumain}/520 pts`, 185, yPosition + 5.5, { align: 'right' });
    
    yPosition += 13; // Augmenté pour plus d'espace après l'en-tête
    
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...textColor);
    
    // Détails Capital Humain
    // Calculer les points de français oral et écrit
    let frenchOralPoints = 0;
    let frenchWrittenPoints = 0;
    
    if (frenchTestType && frenchTestType !== 'manual') {
      const oralCompLevel = convertTestScoreToQuebecLevel(rawScoreOralComp, frenchTestType, 'comp');
      const oralProdLevel = convertTestScoreToQuebecLevel(rawScoreOralProd, frenchTestType, 
        frenchTestType === 'tef-canada-new' ? 'oral-prod' : 'expr');
      const writtenCompLevel = convertTestScoreToQuebecLevel(rawScoreWrittenComp, frenchTestType, 'comp');
      const writtenProdLevel = convertTestScoreToQuebecLevel(rawScoreWrittenProd, frenchTestType, 'written');
      
      frenchOralPoints = getFrenchPoints(oralCompLevel, hasSpouse, 'oral') + getFrenchPoints(oralProdLevel, hasSpouse, 'oral');
      frenchWrittenPoints = getFrenchPoints(writtenCompLevel, hasSpouse, 'written') + getFrenchPoints(writtenProdLevel, hasSpouse, 'written');
    } else {
      frenchOralPoints = getFrenchPoints(frenchOralComp, hasSpouse, 'oral') + getFrenchPoints(frenchOralProd, hasSpouse, 'oral');
      frenchWrittenPoints = getFrenchPoints(frenchWrittenComp, hasSpouse, 'written') + getFrenchPoints(frenchWrittenProd, hasSpouse, 'written');
    }
    
    // Encadré avec fond pour les détails (version compacte)
    const drawDetailLine = (label, points, maxPoints, y) => {
      doc.setFontSize(9);
      doc.setTextColor(...textColor);
      doc.text(`• ${label}`, 20, y);
      doc.setFont(undefined, 'bold');
      doc.text(`${points}`, 170, y, { align: 'right' });
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...mediumGray);
      doc.setFontSize(8);
      doc.text(`/ ${maxPoints}`, 172, y);
      
      // Barre de progression
      const barWidth = 20;
      const progress = Math.min(points / maxPoints, 1);
      doc.setDrawColor(...lightGray);
      doc.setLineWidth(2);
      doc.line(180, y - 1, 180 + barWidth, y - 1);
      if (progress > 0) {
        doc.setDrawColor(...accentColor);
        doc.line(180, y - 1, 180 + (barWidth * progress), y - 1);
      }
    };
    
    drawDetailLine(`Français (oral)`, frenchOralPoints, 100, yPosition);
    yPosition += 4;
    drawDetailLine(`Français (écrit)`, frenchWrittenPoints, 100, yPosition);
    yPosition += 4;
    drawDetailLine(`Âge`, agePoints, 120, yPosition);
    yPosition += 4;
    drawDetailLine(`Scolarité`, educationPoints, 140, yPosition);
    yPosition += 4;
    drawDetailLine(`Diplôme Québec`, quebecDiplomaPoints, 30, yPosition);
    yPosition += 4;
    drawDetailLine(`Expérience travail QC`, workExpQcPoints, 30, yPosition);
    yPosition += 8;
    
    // 2. BESOINS DU QUÉBEC
    doc.setFillColor(...primaryColor);
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.rect(15, yPosition, 180, 8, 'FD');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(11);
    doc.text(`2. BESOINS DU QUÉBEC`, 20, yPosition + 5.5);
    doc.text(`${besoinsQuebec}/580 pts`, 185, yPosition + 5.5, { align: 'right' });
    
    yPosition += 13; // Augmenté pour espacer du haut
    
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...textColor);
    
    drawDetailLine(`Profession`, professionPoints, 100, yPosition);
    yPosition += 4;
    drawDetailLine(`Diplôme Québec validé`, quebecDiplomaPoints, 30, yPosition);
    yPosition += 4;
    drawDetailLine(`Expérience QC (profession)`, workExpQcPoints, 100, yPosition);
    yPosition += 4;
    drawDetailLine(`Hors RMM`, outsideCMMPoints, 120, yPosition);
    yPosition += 4;
    drawDetailLine(`Offre d'emploi validée`, jobOfferPoints, 50, yPosition);
    yPosition += 4;
    drawDetailLine(`Autorisation profession`, professionAuthPoints, 50, yPosition);
    yPosition += 8;
    
    // 3. FACTEURS D'ADAPTATION
    doc.setFillColor(...primaryColor);
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.rect(15, yPosition, 180, 8, 'FD');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(11);
    doc.text(`3. FACTEURS D'ADAPTATION`, 20, yPosition + 5.5);
    doc.text(`${facteursAdaptation}/180 pts`, 185, yPosition + 5.5, { align: 'right' });
    
    yPosition += 13; // Augmenté pour espacer du haut
    
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...textColor);
    
    drawDetailLine(`Études au Québec`, studyQcPoints, 30, yPosition);
    yPosition += 4;
    drawDetailLine(`Famille au Québec`, relativePoints, 10, yPosition);
    yPosition += 4;
    
    if (hasSpouse) {
      const totalSpousePoints = spouseFrenchPoints + spouseAgePoints + spouseWorkExpQcPoints + spouseEducationPoints + spouseQuebecDiplomaPoints;
      drawDetailLine(`Conjoint accompagnant`, totalSpousePoints, 140, yPosition);
      yPosition += 6;
      
      // Détails conjoint dans un encadré (version compacte)
      doc.setFillColor(250, 250, 250);
      doc.setDrawColor(...lightGray);
      doc.setLineWidth(0.3);
      doc.rect(25, yPosition - 2, 165, 14, 'FD');
      
      doc.setFontSize(8);
      doc.setTextColor(...textColor);
      doc.text(`Français: ${spouseFrenchPoints}/40`, 30, yPosition + 2);
      doc.text(`Âge: ${spouseAgePoints}/30`, 30, yPosition + 6);
      doc.text(`Exp. QC: ${spouseWorkExpQcPoints}/30`, 30, yPosition + 10);
      doc.text(`Scolarité: ${spouseEducationPoints}/30`, 90, yPosition + 2);
      doc.text(`Diplôme QC: ${spouseQuebecDiplomaPoints}/10`, 90, yPosition + 6);
      
      yPosition += 17;
    } else {
      yPosition += 6;
    }
    
    // Note et disclaimer (version compacte - toujours sur la même page)
    yPosition += 3;
    
    doc.setFillColor(255, 248, 220);
    doc.setDrawColor(255, 193, 7);
    doc.setLineWidth(0.5);
    doc.rect(15, yPosition, 180, 12, 'FD');
    
    doc.setFontSize(8);
    doc.setTextColor(...textColor);
    doc.setFont(undefined, 'italic');
    doc.text('Note importante :', 20, yPosition + 4);
    doc.setFont(undefined, 'normal');
    
    // Texte avec retour à la ligne automatique
    const noteText = "Ce document constitue une estimation basée sur les informations fournies. Elle a été préparée avec soin selon les critères officiels en vigueur, afin de vous offrir une projection fiable; la décision finale revient toutefois aux autorités compétentes.";
    const splitNote = doc.splitTextToSize(noteText, 170);
    doc.text(splitNote, 20, yPosition + 7);
    
    // Pied de page en bas de la page
    const pageHeight = doc.internal.pageSize.getHeight();
    
    doc.setFillColor(...primaryColor);
    doc.rect(0, pageHeight - 20, 210, 20, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text(companyName, 15, pageHeight - 13);
    doc.setFont(undefined, 'normal');
    doc.text('Services d\'immigration professionnels', 15 + doc.getTextWidth(companyName) + 5, pageHeight - 13);
    doc.setFontSize(7);
    doc.text('Ce rapport est confidentiel et réservé exclusivement à l\'usage du destinataire.', 15, pageHeight - 7);
    
    doc.setTextColor(200, 200, 200);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`, 195, pageHeight - 5, { align: 'right' });

    
    // Nom de fichier avec date et nom du client
    const fileName = selectedPerson?.label 
      ? `Evaluation_PSTQ_${selectedPerson.label.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
      : `Evaluation_PSTQ_Quebec_${new Date().toISOString().split('T')[0]}.pdf`;
    
    doc.save(fileName);
  };

  return (
    <div className="module-content" style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <i className="fas fa-calculator" style={{ fontSize: '2rem', color: '#2563eb' }}></i>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', margin: 0 }}>
            Calculateur PSTQ Québec
          </h1>
        </div>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
          Programme de sélection des travailleurs qualifiés du Québec
        </p>
        <div style={{ padding: '1rem', background: '#eff6ff', borderRadius: '8px' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <i className="fas fa-info-circle" style={{ color: '#2563eb', marginTop: '0.125rem' }}></i>
            <p style={{ fontSize: '0.875rem', margin: 0 }}>
              Ce calculateur vous aide à estimer votre pointage pour le système de déclaration d'intérêt Arrima. 
              Le pointage maximum est de 1400 points.
            </p>
          </div>
        </div>
      </div>

      {/* Sélection du client / lead */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
          Calculer pour un client ou un lead (optionnel)
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={selectedPerson ? selectedPerson.label : personQuery}
            onChange={(e) => { setSelectedPerson(null); setPersonQuery(e.target.value); }}
            placeholder="Rechercher par nom, email ou téléphone..."
            className="form-control"
            style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '0.375rem', padding: '0.625rem 0.75rem' }}
          />
          {(personQuery && !selectedPerson) && (
            <div style={{ position: 'absolute', zIndex: 10, top: '2.75rem', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.375rem', maxHeight: '260px', overflowY: 'auto', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
              {isSearching && (
                <div style={{ padding: '0.625rem 0.75rem', fontSize: '0.8125rem', color: '#6b7280' }}>Recherche...</div>
              )}
              {!isSearching && personResults.length === 0 && (
                <div style={{ padding: '0.625rem 0.75rem', fontSize: '0.8125rem', color: '#6b7280' }}>Aucun résultat</div>
              )}
              {personResults.map((r) => (
                <div
                  key={`${r.type}-${r.id}`}
                  onClick={() => { setSelectedPerson(r); setPersonQuery(''); }}
                  style={{ padding: '0.625rem 0.75rem', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span>{r.label}</span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{r.type}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedPerson && (
          <div style={{ marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#f3f4f6', borderRadius: '999px', padding: '0.375rem 0.625rem', fontSize: '0.8125rem', color: '#374151' }}>
            <span style={{ fontWeight: 600 }}>{selectedPerson.type === 'client' ? 'Client' : 'Lead'}:</span>
            <span>{selectedPerson.label}</span>
            <button onClick={() => setSelectedPerson(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.25rem', lineHeight: 1 }}>×</button>
          </div>
        )}
      </div>

      {/* Situation familiale */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-users" style={{ color: '#2563eb' }}></i>
          Situation familiale
        </h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hasSpouse}
            onChange={(e) => setHasSpouse(e.target.checked)}
            style={{ width: '1.25rem', height: '1.25rem' }}
          />
          <span>J'ai un époux/épouse ou conjoint(e) de fait qui m'accompagne</span>
        </label>
      </div>

      {/* Score total */}
      <div className="pstq-score-card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ opacity: 0.9, marginBottom: '0.25rem' }}>Pointage total</p>
            <p style={{ fontSize: '3rem', fontWeight: 'bold', margin: 0 }}>{totalPoints}</p>
            <p style={{ opacity: 0.9, marginTop: '0.25rem' }}>sur 1400 points maximum</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <i className="fas fa-award" style={{ fontSize: '5rem', opacity: 0.3 }}></i>
            <button
              onClick={generatePDF}
              className="btn btn-light"
              style={{
                background: 'white',
                color: '#2563eb',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <i className="fas fa-download"></i>
              Télécharger PDF
            </button>
          </div>
        </div>
        <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', textAlign: 'center' }}>
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.75rem' }}>
            <p style={{ opacity: 0.9, fontSize: '0.875rem' }}>Capital humain</p>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{capitalHumain}</p>
            <p style={{ opacity: 0.9, fontSize: '0.75rem' }}>/520</p>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.75rem' }}>
            <p style={{ opacity: 0.9, fontSize: '0.875rem' }}>Besoins QC</p>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{besoinsQuebec}</p>
            <p style={{ opacity: 0.9, fontSize: '0.75rem' }}>/700</p>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.75rem' }}>
            <p style={{ opacity: 0.9, fontSize: '0.875rem' }}>Adaptation</p>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{facteursAdaptation}</p>
            <p style={{ opacity: 0.9, fontSize: '0.75rem' }}>/180</p>
          </div>
        </div>
      </div>

      {/* Modal Guide des professions */}
      {showProfessionGuide && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '80rem',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)', padding: '1.5rem', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                  <i className="fas fa-search"></i>
                  Guide des professions CNP 2021
                </h2>
                <button
                  onClick={() => setShowProfessionGuide(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    width: '2.5rem',
                    height: '2.5rem'
                  }}
                >
                  ✕
                </button>
              </div>
              <p style={{ opacity: 0.9, fontSize: '0.875rem', margin: 0 }}>
                Diagnostic officiel MIFI 2024-2028
              </p>
            </div>
            
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ position: 'relative' }}>
                  <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '0.75rem', color: '#9ca3af' }}></i>
                  <input
                    type="text"
                    placeholder="Rechercher par profession, code CNP ou catégorie..."
                    value={professionSearch}
                    onChange={(e) => setProfessionSearch(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '2.5rem',
                      paddingRight: '1rem',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px'
                    }}
                  />
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  {filteredProfessions.length} profession(s) trouvée(s)
                </p>
              </div>
              
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                <h3 style={{ fontWeight: '600', marginBottom: '0.75rem' }}>Légende des diagnostics :</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '0.75rem', height: '0.75rem', background: '#ef4444', borderRadius: '50%' }}></span>
                    <span><strong>Déficit</strong> : 90-120 points</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '0.75rem', height: '0.75rem', background: '#eab308', borderRadius: '50%' }}></span>
                    <span><strong>Léger déficit</strong> : 70-100 points</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '0.75rem', height: '0.75rem', background: '#9ca3af', borderRadius: '50%' }}></span>
                    <span><strong>Équilibre</strong> : 5-25 points</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '0.75rem', height: '0.75rem', background: '#3b82f6', borderRadius: '50%' }}></span>
                    <span><strong>Léger surplus</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '0.75rem', height: '0.75rem', background: '#6b7280', borderRadius: '50%' }}></span>
                    <span><strong>Non publié</strong></span>
                  </div>
                </div>
              </div>
              
              {Object.entries(professionsByCategory).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                  <i className="fas fa-search" style={{ fontSize: '3rem', opacity: 0.5, marginBottom: '0.75rem' }}></i>
                  <p>Aucune profession trouvée pour "{professionSearch}"</p>
                </div>
              ) : (
                Object.entries(professionsByCategory).map(([categorie, profs]) => (
                  <div key={categorie} style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontWeight: 'bold', fontSize: '1.125rem', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '2px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{categorie}</span>
                      <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 'normal' }}>({profs.length})</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {profs.map(prof => (
                        <div 
                          key={prof.code}
                          style={{ padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                <span style={{
                                  width: '0.75rem',
                                  height: '0.75rem',
                                  borderRadius: '50%',
                                  background: prof.diagnostic === 'deficit' ? '#ef4444' :
                                            prof.diagnostic === 'leger' ? '#eab308' :
                                            prof.diagnostic === 'surplus' ? '#3b82f6' :
                                            prof.diagnostic === 'non_publie' ? '#6b7280' : '#9ca3af'
                                }}></span>
                                <span style={{ fontWeight: '500' }}>{prof.titre}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                <span style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '0.125rem 0.5rem', borderRadius: '4px' }}>
                                  CNP {prof.code}
                                </span>
                                <span style={{
                                  fontWeight: '500',
                                  color: prof.diagnostic === 'deficit' ? '#dc2626' :
                                        prof.diagnostic === 'leger' ? '#ca8a04' :
                                        prof.diagnostic === 'surplus' ? '#2563eb' :
                                        prof.diagnostic === 'non_publie' ? '#6b7280' : '#6b7280'
                                }}>
                                  {prof.diagnostic === 'deficit' ? '🔴 Déficit' :
                                   prof.diagnostic === 'leger' ? '🟡 Léger déficit' :
                                   prof.diagnostic === 'surplus' ? '🔵 Léger surplus' :
                                   prof.diagnostic === 'non_publie' ? '⚫ Non publié' : '⚪ Équilibre'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Capital humain */}
      <Section 
        title="Capital humain" 
        icon="fas fa-user" 
        points={capitalHumain} 
        maxPoints={520}
        id="capital"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Français */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Connaissance du français ({frenchPoints}/200 points)
            </h3>
            
            {/* Sélection du type de test */}
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#eff6ff', borderRadius: '6px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#1e40af', marginBottom: '0.5rem', fontWeight: '500' }}>
                📝 Quel test de français avez-vous passé?
              </label>
              <select 
                value={frenchTestType} 
                onChange={(e) => setFrenchTestType(e.target.value)}
                className="form-control"
                style={{ background: 'white' }}
              >
                <option value="">-- Sélectionnez votre test --</option>
                <option value="tef">TEF (Test d'Évaluation de Français)</option>
                <option value="tefaq">TEFAQ (Québec)</option>
                <option value="tef-canada-old">TEF-Canada (avant le 11 décembre 2023)</option>
                <option value="tef-canada-new">TEF-Canada (à partir du 11 décembre 2023)</option>
                <option value="tcf">TCF (Test de Connaissance du Français)</option>
                <option value="tcf-quebec">TCF-Québec</option>
                <option value="tcf-canada">TCF-Canada</option>
                <option value="manual">⚙️ Saisie manuelle (niveau québécois)</option>
              </select>
              {frenchTestType && frenchTestType !== 'manual' && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
                  💡 Entrez vos scores du test ci-dessous. La conversion sera automatique.
                </p>
              )}
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Compréhension orale {frenchTestType && frenchTestType !== 'manual' && '(Score)'}
                </label>
                {(!frenchTestType || frenchTestType === 'manual') ? (
                  <select 
                    value={frenchOralComp} 
                    onChange={(e) => setFrenchOralComp(e.target.value)}
                    className="form-control"
                  >
                    <option value="0">Aucun</option>
                    <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                    <option value="5">Niveau 5-6 (B1) - {hasSpouse ? '30' : '38'} pts</option>
                    <option value="7">Niveau 7-8 (B2) - {hasSpouse ? '35' : '44'} pts</option>
                    <option value="9">Niveau 9-10 (C1) - {hasSpouse ? '38' : '50'} pts</option>
                    <option value="11">Niveau 11-12 (C2) - {hasSpouse ? '40' : '50'} pts</option>
                  </select>
                ) : (
                  <NumericInput
                    id="raw-score-oral-comp"
                    value={rawScoreOralComp}
                    onChange={setRawScoreOralComp}
                    placeholder={frenchTestType.includes('tcf') ? 'Ex: 450' : 'Ex: 350'}
                    className="form-control"
                  />
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Expression orale {frenchTestType && frenchTestType !== 'manual' && '(Score)'}
                </label>
                {(!frenchTestType || frenchTestType === 'manual') ? (
                  <select 
                    value={frenchOralProd} 
                    onChange={(e) => setFrenchOralProd(e.target.value)}
                    className="form-control"
                  >
                    <option value="0">Aucun</option>
                    <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                    <option value="5">Niveau 5-6 (B1) - {hasSpouse ? '30' : '38'} pts</option>
                    <option value="7">Niveau 7-8 (B2) - {hasSpouse ? '35' : '44'} pts</option>
                    <option value="9">Niveau 9-10 (C1) - {hasSpouse ? '38' : '50'} pts</option>
                    <option value="11">Niveau 11-12 (C2) - {hasSpouse ? '40' : '50'} pts</option>
                  </select>
                ) : (
                  <NumericInput
                    id="raw-score-oral-prod"
                    value={rawScoreOralProd}
                    onChange={setRawScoreOralProd}
                    placeholder={frenchTestType.includes('tcf') ? 'Ex: 12 (note 1-20)' : 'Ex: 400'}
                    className="form-control"
                  />
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Compréhension écrite {frenchTestType && frenchTestType !== 'manual' && '(Score)'}
                </label>
                {(!frenchTestType || frenchTestType === 'manual') ? (
                  <select 
                    value={frenchWrittenComp} 
                    onChange={(e) => setFrenchWrittenComp(e.target.value)}
                    className="form-control"
                  >
                    <option value="0">Aucun</option>
                    <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                    <option value="5">Niveau 5-6 (B1) - {hasSpouse ? '30' : '38'} pts</option>
                    <option value="7">Niveau 7-8 (B2) - {hasSpouse ? '35' : '44'} pts</option>
                    <option value="9">Niveau 9-10 (C1) - {hasSpouse ? '38' : '50'} pts</option>
                    <option value="11">Niveau 11-12 (C2) - {hasSpouse ? '40' : '50'} pts</option>
                  </select>
                ) : (
                  <NumericInput
                    id="raw-score-written-comp"
                    value={rawScoreWrittenComp}
                    onChange={setRawScoreWrittenComp}
                    placeholder={frenchTestType.includes('tcf') ? 'Ex: 500' : 'Ex: 450'}
                    className="form-control"
                  />
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Expression écrite {frenchTestType && frenchTestType !== 'manual' && '(Score)'}
                </label>
                {(!frenchTestType || frenchTestType === 'manual') ? (
                  <select 
                    value={frenchWrittenProd} 
                    onChange={(e) => setFrenchWrittenProd(e.target.value)}
                    className="form-control"
                  >
                    <option value="0">Aucun</option>
                    <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                    <option value="5">Niveau 5-6 (B1) - {hasSpouse ? '30' : '38'} pts</option>
                    <option value="7">Niveau 7-8 (B2) - {hasSpouse ? '35' : '44'} pts</option>
                    <option value="9">Niveau 9-10 (C1) - {hasSpouse ? '38' : '50'} pts</option>
                    <option value="11">Niveau 11-12 (C2) - {hasSpouse ? '40' : '50'} pts</option>
                  </select>
                ) : (
                  <NumericInput
                    id="raw-score-written-prod"
                    value={rawScoreWrittenProd}
                    onChange={setRawScoreWrittenProd}
                    placeholder={frenchTestType.includes('tcf') ? 'Ex: 15 (note 1-20)' : 'Ex: 500'}
                    className="form-control"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Âge */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Âge ({agePoints}/{hasSpouse ? 100 : 120} points)
            </h3>
            <NumericInput
              id="age-principal"
              value={age}
              onChange={setAge}
              placeholder="Entrez votre âge"
              className="form-control"
            />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              Maximum de points entre 20-30 ans
            </p>
          </div>

          {/* Expérience */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Durée de l'expérience de travail ({workExpPoints}/{hasSpouse ? 50 : 70} points)
            </h3>
            <select 
              value={workExp} 
              onChange={(e) => setWorkExp(e.target.value)}
              className="form-control"
            >
              <option value="0">Moins de 12 mois</option>
              <option value="12">12-23 mois ({hasSpouse ? '15' : '20'} pts)</option>
              <option value="24">24-35 mois ({hasSpouse ? '30' : '40'} pts)</option>
              <option value="36">36-47 mois ({hasSpouse ? '35' : '50'} pts)</option>
              <option value="48">48 mois et plus ({hasSpouse ? '50' : '70'} pts)</option>
            </select>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              Dans les 5 dernières années, n'importe où dans le monde
            </p>
          </div>

          {/* Scolarité */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Niveau de scolarité ({educationPoints}/{hasSpouse ? 110 : 130} points)
            </h3>
            <select 
              value={education} 
              onChange={(e) => setEducation(e.target.value)}
              className="form-control"
            >
              <option value="none">Aucun diplôme</option>
              <optgroup label="Secondaire">
                <option value="secondaire">Secondaire général complété ({hasSpouse ? '11' : '13'} pts)</option>
                <option value="sec_prof_600_899">Secondaire professionnel 600 à 899 heures ({hasSpouse ? '11' : '13'} pts)</option>
                <option value="sec_prof_900_qc">Secondaire professionnel 900h+ (diplôme du Québec) ({hasSpouse ? '22' : '26'} pts)</option>
                <option value="sec_prof_1an">Secondaire professionnel 1 an ou plus à temps plein (diplôme hors Québec) ({hasSpouse ? '22' : '26'} pts)</option>
              </optgroup>
              <optgroup label="Postsecondaire">
                <option value="post_gen_2ans">Postsecondaire général 2 ans à temps plein ({hasSpouse ? '33' : '39'} pts)</option>
                <option value="post_tech_900_qc">Postsecondaire technique 900h+ (diplôme du Québec) ({hasSpouse ? '44' : '52'} pts)</option>
                <option value="post_tech_1_2ans">Postsecondaire technique 1 ou 2 ans à temps plein (diplôme hors Québec) ({hasSpouse ? '44' : '52'} pts)</option>
                <option value="post_tech_3ans">Postsecondaire technique 3 ans à temps plein ({hasSpouse ? '66' : '78'} pts)</option>
              </optgroup>
              <optgroup label="Universitaire 1er cycle">
                <option value="univ_1er_1an">Universitaire 1er cycle 1 an à temps plein ({hasSpouse ? '66' : '78'} pts)</option>
                <option value="univ_1er_2ans">Universitaire 1er cycle 2 ans à temps plein ({hasSpouse ? '77' : '91'} pts)</option>
                <option value="univ_1er_3_4ans">Universitaire 1er cycle 3 ou 4 ans à temps plein ({hasSpouse ? '88' : '104'} pts)</option>
                <option value="univ_1er_5ans">Universitaire 1er cycle 5 ans ou plus à temps plein ({hasSpouse ? '93' : '110'} pts)</option>
              </optgroup>
              <optgroup label="Universitaire 2e cycle">
                <option value="univ_2e_1an">Universitaire 2e cycle 1 an à temps plein ({hasSpouse ? '93' : '110'} pts)</option>
                <option value="univ_2e_2ans">Universitaire 2e cycle 2 ans ou plus à temps plein ({hasSpouse ? '99' : '117'} pts)</option>
              </optgroup>
              <optgroup label="Universitaire médical et 3e cycle">
                <option value="univ_med_2ans">Universitaire dans une spécialisation médicale 2 ans ou plus à temps plein ({hasSpouse ? '110' : '130'} pts)</option>
                <option value="univ_3e">Universitaire 3e cycle ({hasSpouse ? '110' : '130'} pts)</option>
              </optgroup>
            </select>
          </div>
        </div>
      </Section>

      {/* Besoins du Québec */}
      <Section 
        title="Réponse aux besoins du Québec" 
        icon="fas fa-briefcase" 
        points={besoinsQuebec} 
        maxPoints={700}
        id="besoins"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Diagnostic profession */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Diagnostic de main-d'œuvre et expérience ({professionPoints}/120 points)
            </h3>
            <div style={{ padding: '0.75rem', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', marginBottom: '0.75rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                <strong>💡 Important :</strong> Cette expérience peut être acquise N'IMPORTE OÙ dans le monde 
                (pas seulement au Québec).
              </p>
            </div>
            
            <button
              onClick={() => setShowProfessionGuide(true)}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              <i className="fas fa-search"></i>
              📋 Voir toutes les professions et diagnostics
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Diagnostic de votre profession principale
                </label>
                <select 
                  value={professionDiag} 
                  onChange={(e) => setProfessionDiag(e.target.value)}
                  className="form-control"
                >
                  <option value="equilibre">⚪ Équilibre (5-25 pts)</option>
                  <option value="leger">🟡 Léger déficit (70-100 pts)</option>
                  <option value="deficit">🔴 Déficit (90-120 pts)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Durée d'expérience dans votre profession
                </label>
                <select 
                  value={professionExpQc} 
                  onChange={(e) => setProfessionExpQc(e.target.value)}
                  className="form-control"
                >
                  <option value="0">Moins de 12 mois</option>
                  <option value="12">12-23 mois</option>
                  <option value="24">24-35 mois</option>
                  <option value="36">36-47 mois</option>
                  <option value="48">48 mois et plus</option>
                </select>
              </div>
            </div>
          </div>

          {/* Diplôme du Québec */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Diplôme du Québec ({quebecDiplomaPoints}/200 points)
            </h3>
            <select 
              value={quebecDiploma} 
              onChange={(e) => setQuebecDiploma(e.target.value)}
              className="form-control"
            >
              <option value="none">Aucun diplôme du Québec</option>
              <optgroup label="Secondaire">
                <option value="secondaire">Secondaire général (20 pts)</option>
                <option value="sec_prof_600_899">Secondaire professionnel 600 à 899 heures (20 pts)</option>
                <option value="sec_prof_900">Secondaire professionnel 900h+ (40 pts)</option>
              </optgroup>
              <optgroup label="Postsecondaire">
                <option value="post_gen_2ans">Postsecondaire général 2 ans (60 pts)</option>
                <option value="post_tech_900">Postsecondaire technique 900h+ (80 pts)</option>
                <option value="post_tech_3ans">Postsecondaire technique 3 ans (120 pts)</option>
              </optgroup>
              <optgroup label="Universitaire">
                <option value="univ_1er_1an">Universitaire 1er cycle 1 an (120 pts)</option>
                <option value="univ_1er_2ans">Universitaire 1er cycle 2 ans (140 pts)</option>
                <option value="univ_1er_3_4ans">Universitaire 1er cycle 3-4 ans (160 pts)</option>
                <option value="univ_1er_5ans">Universitaire 1er cycle 5 ans+ (170 pts)</option>
                <option value="univ_2e_1an">Universitaire 2e cycle 1 an (170 pts)</option>
                <option value="univ_2e_2ans">Universitaire 2e cycle 2 ans+ (180 pts)</option>
                <option value="univ_med_2ans">Universitaire spécialisation médicale 2 ans+ (200 pts)</option>
                <option value="univ_3e">Universitaire 3e cycle (200 pts)</option>
              </optgroup>
            </select>
          </div>

          {/* Expérience au Québec */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Expérience de travail AU QUÉBEC ({workExpQcPoints}/160 points)
            </h3>
            <div style={{ padding: '0.75rem', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '6px', marginBottom: '0.75rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#065f46', margin: 0 }}>
                <strong>✅ Bonus :</strong> Expérience acquise physiquement au Québec uniquement
              </p>
            </div>
            <select 
              value={workExpQc} 
              onChange={(e) => setWorkExpQc(e.target.value)}
              className="form-control"
            >
              <option value="0">Aucune ou moins de 12 mois</option>
              <option value="12">12-23 mois (40 pts)</option>
              <option value="24">24-35 mois (80 pts)</option>
              <option value="36">36-47 mois (120 pts)</option>
              <option value="48">48 mois et plus (160 pts)</option>
            </select>
          </div>

          {/* Offre d'emploi */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Offre d'emploi validée ({jobOfferPoints}/50 points)
            </h3>
            <select 
              value={jobOffer} 
              onChange={(e) => setJobOffer(e.target.value)}
              className="form-control"
            >
              <option value="none">Aucune offre</option>
              <option value="inside_cmm">Dans la région de Montréal (30 pts)</option>
              <option value="outside_cmm">Hors région de Montréal (50 pts)</option>
            </select>
          </div>
        </div>
      </Section>

      {/* Facteurs d'adaptation */}
      <Section 
        title="Facteurs d'adaptation" 
        icon="fas fa-home" 
        points={facteursAdaptation} 
        maxPoints={180}
        id="adaptation"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Séjour d'études */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Séjour d'études au Québec ({studyQcPoints}/30 points)
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Statut
                </label>
                <select 
                  value={studyQc} 
                  onChange={(e) => setStudyQc(e.target.value)}
                  className="form-control"
                >
                  <option value="none">Aucun séjour d'études</option>
                  <option value="completed">Séjour terminé (sans diplôme)</option>
                  <option value="ongoing">Séjour en cours</option>
                </select>
              </div>
              {studyQc !== 'none' && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Durée
                  </label>
                  <select 
                    value={studyQcDuration} 
                    onChange={(e) => setStudyQcDuration(e.target.value)}
                    className="form-control"
                  >
                    <option value="0">Moins de 6 mois</option>
                    <option value="6">6-11 mois</option>
                    <option value="12">12-23 mois</option>
                    <option value="24">24-35 mois</option>
                    <option value="36">36-47 mois</option>
                    <option value="48">48 mois et plus</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Parenté */}
          <div>
            <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Membre de la parenté au Québec ({relativePoints}/10 points)
            </h3>
            <select 
              value={relative} 
              onChange={(e) => setRelative(e.target.value)}
              className="form-control"
            >
              <option value="none">Aucun</option>
              <option value="direct">Lié directement à moi (10 pts)</option>
              <option value="spouse">Lié à mon conjoint (5 pts)</option>
            </select>
          </div>

          {/* Conjoint */}
          {hasSpouse && (
            <>
              <div style={{ paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                <h3 style={{ fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="fas fa-users" style={{ color: '#2563eb' }}></i>
                  Informations sur le conjoint
                </h3>
              </div>

              <div>
                <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
                  Français du conjoint ({spouseFrenchPoints}/40 points)
                </h3>
                
                {/* Sélection du type de test pour conjoint */}
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#eff6ff', borderRadius: '6px' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', color: '#1e40af', marginBottom: '0.5rem', fontWeight: '500' }}>
                    📝 Quel test de français le conjoint a-t-il passé?
                  </label>
                  <select 
                    value={spouseFrenchTestType} 
                    onChange={(e) => setSpouseFrenchTestType(e.target.value)}
                    className="form-control"
                    style={{ background: 'white' }}
                  >
                    <option value="">-- Sélectionnez votre test --</option>
                    <option value="tef">TEF (Test d'Évaluation de Français)</option>
                    <option value="tefaq">TEFAQ (Québec)</option>
                    <option value="tef-canada-old">TEF-Canada (avant le 11 décembre 2023)</option>
                    <option value="tef-canada-new">TEF-Canada (à partir du 11 décembre 2023)</option>
                    <option value="tcf">TCF (Test de Connaissance du Français)</option>
                    <option value="tcf-quebec">TCF-Québec</option>
                    <option value="tcf-canada">TCF-Canada</option>
                    <option value="manual">⚙️ Saisie manuelle (niveau québécois)</option>
                  </select>
                  {spouseFrenchTestType && spouseFrenchTestType !== 'manual' && (
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
                      💡 Entrez vos scores du test ci-dessous. La conversion sera automatique.
                    </p>
                  )}
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Compréhension orale {spouseFrenchTestType && spouseFrenchTestType !== 'manual' && '(Score)'}
                    </label>
                    {(!spouseFrenchTestType || spouseFrenchTestType === 'manual') ? (
                      <select 
                        value={spouseFrenchOralComp} 
                        onChange={(e) => setSpouseFrenchOralComp(e.target.value)}
                        className="form-control"
                      >
                        <option value="0">Aucun</option>
                        <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                        <option value="5">Niveau 5-6 (B1) - 6 pts</option>
                        <option value="7">Niveau 7-8 (B2) - 8 pts</option>
                        <option value="9">Niveau 9-10 (C1) - 9 pts</option>
                        <option value="11">Niveau 11-12 (C2) - 10 pts</option>
                      </select>
                    ) : (
                      <NumericInput
                        id="raw-score-spouse-oral-comp"
                        value={rawScoreSpouseOralComp}
                        onChange={setRawScoreSpouseOralComp}
                        placeholder={spouseFrenchTestType.includes('tcf') ? 'Ex: 450' : 'Ex: 350'}
                        className="form-control"
                      />
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Expression orale {spouseFrenchTestType && spouseFrenchTestType !== 'manual' && '(Score)'}
                    </label>
                    {(!spouseFrenchTestType || spouseFrenchTestType === 'manual') ? (
                      <select 
                        value={spouseFrenchOralProd} 
                        onChange={(e) => setSpouseFrenchOralProd(e.target.value)}
                        className="form-control"
                      >
                        <option value="0">Aucun</option>
                        <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                        <option value="5">Niveau 5-6 (B1) - 6 pts</option>
                        <option value="7">Niveau 7-8 (B2) - 8 pts</option>
                        <option value="9">Niveau 9-10 (C1) - 9 pts</option>
                        <option value="11">Niveau 11-12 (C2) - 10 pts</option>
                      </select>
                    ) : (
                      <NumericInput
                        id="raw-score-spouse-oral-prod"
                        value={rawScoreSpouseOralProd}
                        onChange={setRawScoreSpouseOralProd}
                        placeholder={spouseFrenchTestType.includes('tcf') ? 'Ex: 12 (note 1-20)' : 'Ex: 400'}
                        className="form-control"
                      />
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Compréhension écrite {spouseFrenchTestType && spouseFrenchTestType !== 'manual' && '(Score)'}
                    </label>
                    {(!spouseFrenchTestType || spouseFrenchTestType === 'manual') ? (
                      <select 
                        value={spouseFrenchWrittenComp} 
                        onChange={(e) => setSpouseFrenchWrittenComp(e.target.value)}
                        className="form-control"
                      >
                        <option value="0">Aucun</option>
                        <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                        <option value="5">Niveau 5-6 (B1) - 6 pts</option>
                        <option value="7">Niveau 7-8 (B2) - 8 pts</option>
                        <option value="9">Niveau 9-10 (C1) - 9 pts</option>
                        <option value="11">Niveau 11-12 (C2) - 10 pts</option>
                      </select>
                    ) : (
                      <NumericInput
                        id="raw-score-spouse-written-comp"
                        value={rawScoreSpouseWrittenComp}
                        onChange={setRawScoreSpouseWrittenComp}
                        placeholder={spouseFrenchTestType.includes('tcf') ? 'Ex: 500' : 'Ex: 450'}
                        className="form-control"
                      />
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Expression écrite {spouseFrenchTestType && spouseFrenchTestType !== 'manual' && '(Score)'}
                    </label>
                    {(!spouseFrenchTestType || spouseFrenchTestType === 'manual') ? (
                      <select 
                        value={spouseFrenchWrittenProd} 
                        onChange={(e) => setSpouseFrenchWrittenProd(e.target.value)}
                        className="form-control"
                      >
                        <option value="0">Aucun</option>
                        <option value="4">Niveau 1-4 (A1-A2) - 0 pts</option>
                        <option value="5">Niveau 5-6 (B1) - 6 pts</option>
                        <option value="7">Niveau 7-8 (B2) - 8 pts</option>
                        <option value="9">Niveau 9-10 (C1) - 9 pts</option>
                        <option value="11">Niveau 11-12 (C2) - 10 pts</option>
                      </select>
                    ) : (
                      <NumericInput
                        id="raw-score-spouse-written-prod"
                        value={rawScoreSpouseWrittenProd}
                        onChange={setRawScoreSpouseWrittenProd}
                        placeholder={spouseFrenchTestType.includes('tcf') ? 'Ex: 15 (note 1-20)' : 'Ex: 500'}
                        className="form-control"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
                  Âge du conjoint ({spouseAgePoints}/20 points)
                </h3>
                <NumericInput
                  id="age-conjoint"
                  value={spouseAge}
                  onChange={setSpouseAge}
                  placeholder="Entrez l'âge du conjoint"
                  className="form-control"
                />
              </div>
            </>
          )}
        </div>
      </Section>

      {/* Footer */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontWeight: '600', marginBottom: '0.75rem' }}>Notes importantes</h3>
        <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280', paddingLeft: '1.25rem' }}>
          <li>Calculateur basé sur les critères officiels MIFI 2025</li>
          <li>Vérifiez toujours sur Arrima avant de soumettre</li>
          <li><strong>Expérience générale :</strong> acquise n'importe où. <strong>Expérience au Québec :</strong> bonus séparé</li>
        </ul>
      </div>
    </div>
  );
};

export default PSUQCalculator;

